import { lazy, Suspense, useEffect, useState } from "react";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import { Box, CircularProgress, GlobalStyles } from "@mui/material";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import ProtectedRoute from "./Components/Auth/ProtectedRoute";
import { useDemoLock, useDemoTracking } from "./Components/Bars/demoMode";
import LeftDrawer from "./Components/Bars/LeftDrawer";
import NavBar from "./Components/Bars/NavBar/NavBar";
import FullBackdrop from "./Components/Loadings/FullBackdrop/FullBackdrop";
import SimpleBackdrop from "./Components/Loadings/SimpleBackdrop/SimpleBackdrop";
import { darkTheme, theme } from "./Configs/theme";
import { DRAWER_FIXED_MIN_WIDTH, useAppContext } from "./Helpers/Contexts/AppContext";
import { AuthProvider, useAuth } from "./Helpers/Contexts/AuthContext";
import HomePage from "./Pages/HomePage";
import NotFoundPage from "./Pages/NotFoundPage";
import TermsPage from "./Pages/TermsPage";

// CSS
import "./App.css";

// Páginas autenticadas carregadas sob demanda: a landing page não baixa o código do dashboard
const DashboardPage = lazy(() => import("./Pages/DashboardPage"));
const AdminSettingsPage = lazy(() => import("./Pages/AdminSettingsPage"));

// Styles
const mainStyles = {
    position: "fixed",
    top: 0,
    left: 0,
    margin: 0,
    padding: 0,
    zIndex: 3,
    minWidth: "100vw",
    width: "100%",
    minHeight: "100vh",
    height: "100%",
    backgroundColor: "background.default",
    color: "text.primary",
    overflow: "auto",
};

const pageLoadingFallback = (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress color="primary" />
    </Box>
);

// Inner content component to use location hook
function AppContent({ darkMode, setDarkMode }) {
    const location = useLocation();
    const [appContext] = useAppContext();
    // Recupera a aba ativa com memória (URL -> localStorage -> fallback 'geral')
    const [activeDashboardTab, setActiveDashboardTab] = useState(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const urlTab = params.get('tab');
            if (urlTab) return urlTab;
            const saved = localStorage.getItem('em_active_dashboard_tab');
            if (saved) return saved;
        } catch { /* ignora */ }
        return 'geral';
    });

    // Recupera o filtro de localização com memória (URL -> localStorage -> fallback 'todas-lojas')
    const [selectedLocationFilter, setSelectedLocationFilter] = useState(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const urlLoc = params.get('loc');
            if (urlLoc) return urlLoc;
            const saved = localStorage.getItem('em_selected_location_filter');
            if (saved) return saved;
        } catch { /* ignora */ }
        return 'todas-lojas';
    });

    // Persiste a aba ativa e atualiza a URL silenciosamente
    useEffect(() => {
        if (!activeDashboardTab) return;
        try {
            localStorage.setItem('em_active_dashboard_tab', activeDashboardTab);
            if (location.pathname === '/home') {
                const url = new URL(window.location.href);
                if (url.searchParams.get('tab') !== activeDashboardTab) {
                    url.searchParams.set('tab', activeDashboardTab);
                    window.history.replaceState({}, '', url.toString());
                }
            }
        } catch { /* ignora */ }
    }, [activeDashboardTab, location.pathname]);

    // Persiste o filtro de localização e atualiza a URL silenciosamente
    useEffect(() => {
        if (!selectedLocationFilter) return;
        try {
            localStorage.setItem('em_selected_location_filter', selectedLocationFilter);
            if (location.pathname === '/home') {
                const url = new URL(window.location.href);
                if (url.searchParams.get('loc') !== selectedLocationFilter) {
                    url.searchParams.set('loc', selectedLocationFilter);
                    window.history.replaceState({}, '', url.toString());
                }
            }
        } catch { /* ignora */ }
    }, [selectedLocationFilter, location.pathname]);

    // Páginas públicas (landing e termos) não têm menu lateral nem barra superior;
    // a 404 também não, para quem não entrou (o menu do sistema não serve para visitante)
    const { user, loadingAuth } = useAuth();
    const isUnknownPath = !['/home', '/panel/settings', '/painel'].includes(location.pathname);
    const isLandingPage = ['/', '/termos', '/privacidade'].includes(location.pathname)
        || (isUnknownPath && !user && !loadingAuth);

    useDemoTracking(activeDashboardTab, location.pathname === '/home');
    useDemoLock();

    return (
        <>
            {/* LeftDrawer only outside landing page */}
            {!isLandingPage && (
                <LeftDrawer
                    activeDashboardTab={activeDashboardTab}
                    setActiveDashboardTab={setActiveDashboardTab}
                    selectedLocationFilter={selectedLocationFilter}
                    setSelectedLocationFilter={setSelectedLocationFilter}
                />
            )}

            {/* Landing rola pela própria página: rolagem nativa no celular (barra de endereço recolhe) */}
            <Box component={"main"} sx={isLandingPage ? { ...mainStyles, position: "relative", height: "auto", overflow: "visible" } : mainStyles}>
                <Box sx={
                    isLandingPage
                        ? { padding: 0 }
                        : {
                            paddingTop: { xs: 0, md: 1 },
                            paddingBottom: { xs: 0, md: 1 },
                            paddingRight: { xs: 0, md: 1 },
                            paddingLeft: { xs: 0, md: 1 },
                            [`@media (min-width: ${DRAWER_FIXED_MIN_WIDTH}px)`]: { paddingLeft: appContext.drawerOpened ? '280px' : 1 },
                            transition: 'padding-left 0.3s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100vh',
                            // Celular: 100vh inclui a barra de endereço escondida e corta o rodapé
                            '@supports (height: 100dvh)': { height: '100dvh' },
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                        }
                }>
                    <Box sx={
                        isLandingPage
                            ? { flexGrow: 1 }
                            : {
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: 'background.paper',
                                borderRadius: { xs: 0, md: '16px' },
                                // não usar o shorthand `border` responsivo: ele reseta a cor para currentColor
                                borderStyle: 'solid',
                                borderWidth: { xs: 0, md: '1px' },
                                borderColor: 'divider',
                                boxShadow: (theme) => theme.palette.mode === 'dark'
                                    ? '0 0 0 1px rgba(59, 130, 246, 0.15), 0 20px 50px -15px rgba(0, 0, 0, 0.8)'
                                    : '0 4px 20px -2px rgba(0, 0, 0, 0.05)',
                                overflow: 'hidden',
                                position: 'relative',
                                minHeight: 0,
                            }
                    }>
                        {!isLandingPage && (
                            <NavBar darkMode={darkMode} setDarkMode={setDarkMode} />
                        )}
                        <Box sx={isLandingPage ? {} : { p: { xs: 1, sm: 2.5 }, flexGrow: 1, overflowY: 'auto', minHeight: 0, scrollbarGutter: 'stable' }}>
                            <Suspense fallback={pageLoadingFallback}>
                                <Routes>
                                    {/* Pública — landing page / login */}
                                    <Route path="/" element={<HomePage darkMode={darkMode} setDarkMode={setDarkMode} />} />

                                    {/* Pública — Termos de Uso e Política de Privacidade */}
                                    <Route path="/termos" element={<TermsPage />} />
                                    <Route path="/privacidade" element={<TermsPage privacy />} />

                                    {/* Protegida — dashboard principal */}
                                    <Route
                                        path="/home"
                                        element={
                                            <ProtectedRoute>
                                                <DashboardPage
                                                    activeDashboardTab={activeDashboardTab}
                                                    setActiveDashboardTab={setActiveDashboardTab}
                                                    selectedLocationFilter={selectedLocationFilter}
                                                    setSelectedLocationFilter={setSelectedLocationFilter}
                                                />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Configurações Admin */}
                                    <Route
                                        path="/panel/settings"
                                        element={
                                            <ProtectedRoute>
                                                <AdminSettingsPage />
                                            </ProtectedRoute>
                                        }
                                    />

                                    {/* Redirecionamentos legados */}
                                    <Route path="/painel" element={<Navigate to="/home" replace />} />

                                    {/* 404 */}
                                    <Route path="*" element={<NotFoundPage />} />
                                </Routes>
                            </Suspense>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    );
}

// Root Component
function App() {
    // Dark Mode (Azul Profundo) — persiste no localStorage
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('app-darkMode') === 'true');

    return (
        <MuiThemeProvider theme={darkMode ? darkTheme : theme}>
            <EmotionThemeProvider theme={darkMode ? darkTheme : theme}>
                {/* Barra de rolagem padrão do site: fina, discreta e acompanha o tema */}
                <GlobalStyles
                    styles={{
                        '*': { scrollbarWidth: 'thin', scrollbarColor: `${darkMode ? '#3f3f46' : '#cbd5e1'} transparent` },
                        '*::-webkit-scrollbar': { width: 5, height: 5 },
                        '*::-webkit-scrollbar-track': { background: 'transparent' },
                        '*::-webkit-scrollbar-thumb': { background: darkMode ? '#3f3f46' : '#cbd5e1', borderRadius: 10 },
                        '*::-webkit-scrollbar-thumb:hover': { background: darkMode ? '#52525b' : '#94a3b8' },
                    }}
                />
                {/* AuthProvider envolve toda a árvore para disponibilizar sessão e lojas */}
                <AuthProvider>
                    <SimpleBackdrop />
                    <FullBackdrop />
                    <Router>
                        <AppContent darkMode={darkMode} setDarkMode={setDarkMode} />
                    </Router>
                </AuthProvider>
            </EmotionThemeProvider>
        </MuiThemeProvider>
    );
}

export default App;
